import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  Filler,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { normalizeStatusTone } from './Status';
import './Sparkline.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const TONE_COLOR_VARS = {
  good: ['--status-success-ink', '#166534', '--surface-success', 'rgba(22, 101, 52, 0.08)'],
  watch: ['--status-warning-ink', '#92400e', '--surface-warning', 'rgba(146, 64, 14, 0.08)'],
  behind: ['--status-danger-ink', '#b91c1c', '--surface-danger-alpha', 'rgba(185, 28, 28, 0.08)'],
  neutral: ['--status-neutral-ink', '#475569', '--surface-info', 'rgba(71, 85, 105, 0.08)']
};

function readCssVar(name, fallback) {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return fallback;
  }

  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
}

function getToneColors(tone) {
  const [strokeVar, strokeFallback, fillVar, fillFallback] = TONE_COLOR_VARS[tone] || TONE_COLOR_VARS.neutral;
  return {
    stroke: readCssVar(strokeVar, strokeFallback),
    fill: readCssVar(fillVar, fillFallback)
  };
}

function normalizePoints(points = [], fallbackValue = null, maxPoints = 12) {
  const numericPoints = (Array.isArray(points) ? points : [])
    .map(point => Number(point))
    .filter(Number.isFinite);
  const fallback = Number(fallbackValue);
  const seed = numericPoints.length ? numericPoints : (Number.isFinite(fallback) ? [fallback] : [0]);
  const sliced = seed.slice(-maxPoints);

  if (sliced.length >= maxPoints) {
    return sliced;
  }

  const first = sliced[0] ?? 0;
  return [
    ...Array.from({ length: maxPoints - sliced.length }, () => first),
    ...sliced
  ];
}

function Sparkline({ points = [], tone = 'neutral', fallbackValue = null, className = '' }) {
  const normalizedTone = normalizeStatusTone(tone);
  const normalizedPoints = useMemo(
    () => normalizePoints(points, fallbackValue, 12),
    [fallbackValue, points]
  );
  const colors = useMemo(() => getToneColors(normalizedTone), [normalizedTone]);

  const chartData = useMemo(() => ({
    labels: normalizedPoints.map((_, index) => `${index + 1}`),
    datasets: [
      {
        data: normalizedPoints,
        borderColor: colors.stroke,
        backgroundColor: colors.fill,
        borderWidth: 1,
        fill: true,
        pointRadius: 0,
        pointHoverRadius: 0,
        tension: 0.38
      }
    ]
  }), [colors.fill, colors.stroke, normalizedPoints]);

  const chartOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: false }
    },
    interaction: { intersect: false },
    scales: {
      x: {
        display: false,
        grid: { display: false },
        border: { display: false }
      },
      y: {
        display: false,
        grid: { display: false },
        border: { display: false },
        grace: '12%'
      }
    },
    elements: {
      line: { capBezierPoints: true }
    }
  }), []);

  return (
    <div className={`sparkline sparkline--${normalizedTone} ${className}`.trim()} aria-hidden>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default Sparkline;
